/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { agentsBlockOnConnection } from "../funcs/agentsBlockOnConnection.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { AgentConnectionBlock } from "../models/components/agentconnectionblock.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  BlockAgentOnConnectionRequest,
  BlockAgentOnConnectionSecurity,
} from "../models/operations/blockagentonconnection.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type BlockAgentOnConnectionMutationVariables = {
  request: BlockAgentOnConnectionRequest;
  security?: BlockAgentOnConnectionSecurity | undefined;
  options?: RequestOptions;
};

export type BlockAgentOnConnectionMutationData = AgentConnectionBlock;

export type BlockAgentOnConnectionMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * blockAgentOnConnection agents
 *
 * @remarks
 * Block one agent in the caller's organization from the caller's own connection through an upstream OAuth client, across every auth client sharing it. Only the connection owner can do this, and it needs no agent management access. Blocks only; it never grants access.
 */
export function useBlockAgentOnConnectionMutation(
  options?: MutationHookOptions<
    BlockAgentOnConnectionMutationData,
    BlockAgentOnConnectionMutationError,
    BlockAgentOnConnectionMutationVariables
  >,
): UseMutationResult<
  BlockAgentOnConnectionMutationData,
  BlockAgentOnConnectionMutationError,
  BlockAgentOnConnectionMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildBlockAgentOnConnectionMutation(client, options),
    ...options,
  });
}

export function mutationKeyBlockAgentOnConnection(): MutationKey {
  return ["@gram/client", "agents", "blockOnConnection"];
}

export function buildBlockAgentOnConnectionMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: BlockAgentOnConnectionMutationVariables,
  ) => Promise<BlockAgentOnConnectionMutationData>;
} {
  return {
    mutationKey: mutationKeyBlockAgentOnConnection(),
    mutationFn: function blockAgentOnConnectionMutationFn({
      request,
      security,
      options,
    }): Promise<BlockAgentOnConnectionMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(agentsBlockOnConnection(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
